import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { 
  Trophy, Users, Calendar, Clock, ArrowLeft, Crown, Medal,
  ChevronRight, Plus, Gamepad2
} from "lucide-react";

const TournamentPage = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const { user, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [bracket, setBracket] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (tournamentId) {
      fetchTournament();
    }
  }, [tournamentId]);

  const fetchTournament = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tournaments/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournament(response.data.tournament);
      setBracket(response.data.bracket || []);
    } catch (err) {
      toast.error("Could not load tournament");
    } finally {
      setLoading(false);
    }
  };

  const updateMatchResult = async (matchId, winnerId) => {
    setUpdating(true);
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/match/${matchId}/result`, {
        winner_id: winnerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Match result updated");
      fetchTournament();
    } catch (err) {
      toast.error("Could not update match result");
    } finally {
      setUpdating(false);
    }
  };

  const getRoundName = (roundIndex, totalRounds) => {
    const remaining = totalRounds - roundIndex;
    if (remaining === 1) return "Finals";
    if (remaining === 2) return "Semi-Finals";
    if (remaining === 3) return "Quarter-Finals";
    return `Round ${roundIndex + 1}`;
  };

  const renderMatch = (match, roundIndex) => {
    const isHost = tournament?.host_id === user?.id;
    const canEdit = isHost && !match.winner_id && match.player1 && match.player2;

    return (
      <div
        key={match.id}
        className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden mb-4"
      >
        {/* Player 1 */}
        <div
          className={`flex items-center justify-between p-3 border-b border-white/5 ${
            match.winner_id === match.player1?.id ? "bg-[#7a6200]/20" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            {match.player1 ? (
              <>
                {match.player1.avatar ? (
                  <img src={match.player1.avatar} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                    <span className="text-[#7a6200] font-bebas text-sm">
                      {match.player1.name?.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-white font-ubuntu text-sm">{match.player1.name}</span>
              </>
            ) : (
              <span className="text-[#52525B] font-ubuntu text-sm italic">TBD</span>
            )}
          </div>
          {match.winner_id === match.player1?.id && (
            <Crown className="w-4 h-4 text-[#7a6200]" />
          )}
          {canEdit && (
            <button
              onClick={() => updateMatchResult(match.id, match.player1.id)}
              disabled={updating}
              className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
            >
              Winner
            </button>
          )}
        </div>

        {/* VS Divider */}
        <div className="px-3 py-1 text-center text-[#52525B] text-xs">VS</div>

        {/* Player 2 */}
        <div
          className={`flex items-center justify-between p-3 ${
            match.winner_id === match.player2?.id ? "bg-[#7a6200]/20" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            {match.player2 ? (
              <>
                {match.player2.avatar ? (
                  <img src={match.player2.avatar} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                    <span className="text-[#7a6200] font-bebas text-sm">
                      {match.player2.name?.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-white font-ubuntu text-sm">{match.player2.name}</span>
              </>
            ) : (
              <span className="text-[#52525B] font-ubuntu text-sm italic">TBD</span>
            )}
          </div>
          {match.winner_id === match.player2?.id && (
            <Crown className="w-4 h-4 text-[#7a6200]" />
          )}
          {canEdit && (
            <button
              onClick={() => updateMatchResult(match.id, match.player2.id)}
              disabled={updating}
              className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
            >
              Winner
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading tournament...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="tournament-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-ubuntu text-sm">Back to Home</span>
          </button>

          {tournament ? (
            <>
              {/* Tournament Header */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                    <Trophy className="w-8 h-8 text-[#7a6200]" />
                  </div>
                  <div>
                    <h1 className="font-bebas text-4xl tracking-wider text-white">
                      {tournament.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-[#A1A1AA] text-sm">
                      <span className="flex items-center gap-1">
                        <Gamepad2 className="w-4 h-4" />
                        {tournament.game_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {tournament.participant_count} players
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {tournament.date}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        tournament.status === "active" 
                          ? "bg-green-500/20 text-green-400"
                          : tournament.status === "completed"
                          ? "bg-[#7a6200]/20 text-[#7a6200]"
                          : "bg-blue-500/20 text-blue-400"
                      }`}>
                        {tournament.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Winner Display */}
              {tournament.winner && (
                <Card className="bg-gradient-to-r from-[#7a6200]/20 to-transparent border-[#7a6200]/30 mb-8">
                  <CardContent className="p-6 flex items-center gap-4">
                    <Crown className="w-12 h-12 text-[#7a6200]" />
                    <div>
                      <p className="text-[#7a6200] text-sm font-ubuntu">TOURNAMENT CHAMPION</p>
                      <h2 className="font-bebas text-3xl text-white">{tournament.winner.name}</h2>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bracket Display */}
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-8 min-w-max">
                  {bracket.map((round, roundIndex) => (
                    <div key={roundIndex} className="flex-shrink-0 w-64">
                      <h3 className="font-bebas text-lg text-[#7a6200] mb-4 text-center">
                        {getRoundName(roundIndex, bracket.length)}
                      </h3>
                      <div className="space-y-4">
                        {round.matches.map((match) => renderMatch(match, roundIndex))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="py-12 text-center">
                <Trophy className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA] font-ubuntu">Tournament not found</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

// Tournament List Page
const TournamentListPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data.tournaments || []);
    } catch (err) {
      console.log("Could not fetch tournaments");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="tournament-list-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#7a6200]" />
              </div>
              <div>
                <h1 className="font-bebas text-3xl tracking-wider text-white">TOURNAMENTS</h1>
                <p className="text-[#A1A1AA] text-sm font-ubuntu">Compete for glory</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/tournaments/create")}
              className="btn-gold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Tournament
            </Button>
          </div>

          {/* Tournament List */}
          {loading ? (
            <p className="text-center text-[#A1A1AA] py-12">Loading tournaments...</p>
          ) : tournaments.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="py-12 text-center">
                <Trophy className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA] font-ubuntu">No tournaments yet</p>
                <Button
                  onClick={() => navigate("/tournaments/create")}
                  className="btn-outline-gold mt-4"
                >
                  Create First Tournament
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tournaments.map((tournament) => (
                <Card
                  key={tournament.id}
                  className="bg-[#0A0A0A] border-white/10 hover:border-[#7a6200]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tournaments/${tournament.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#7a6200]/20 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-[#7a6200]" />
                        </div>
                        <div>
                          <h3 className="font-bebas text-xl text-white">{tournament.title}</h3>
                          <div className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                            <span>{tournament.game_type}</span>
                            <span>•</span>
                            <span>{tournament.participant_count} players</span>
                            <span>•</span>
                            <span>{tournament.date}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs ${
                          tournament.status === "active" 
                            ? "bg-green-500/20 text-green-400"
                            : tournament.status === "completed"
                            ? "bg-[#7a6200]/20 text-[#7a6200]"
                            : "bg-blue-500/20 text-blue-400"
                        }`}>
                          {tournament.status}
                        </span>
                        <ChevronRight className="w-5 h-5 text-[#52525B]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export { TournamentPage, TournamentListPage };
export default TournamentPage;
